import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

type Post = {
  id: string;
  title: string;
  url: string;
  domain: string | null;
  points: number;
  comments: number;
  image: string | null;
};

const HN_ENDPOINT =
  'https://hn.algolia.com/api/v1/search?tags=front_page&hitsPerPage=8';

const HackerNewsFeed = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(HN_ENDPOINT);
        if (!res.ok) throw new Error('bad response');
        const json = await res.json();
        const hits: Post[] = (json?.hits ?? [])
          .filter((h: any) => h?.title)
          .slice(0, 6)
          .map((h: any) => {
            let domain: string | null = null;
            if (h.url) {
              try {
                domain = new URL(h.url).hostname.replace(/^www\./, '');
              } catch {
                domain = null;
              }
            }
            return {
              id: String(h.objectID),
              title: h.title as string,
              url: h.url || `https://news.ycombinator.com/item?id=${h.objectID}`,
              domain,
              points: h.points ?? 0,
              comments: h.num_comments ?? 0,
              image: domain
                ? `https://www.google.com/s2/favicons?sz=128&domain=${domain}`
                : null,
            };
          });
        if (!active) return;
        if (!hits.length) {
          setState('error');
          return;
        }
        setPosts(hits);
        setState('ready');
      } catch {
        if (active) setState('error');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (state === 'error') return null;

  return (
    <section className="py-24 px-6" aria-label="Trending ideas from Hacker News">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-bebas text-5xl md:text-7xl text-foreground text-center">
          TODAY'S DUMBEST IDEAS ON THE INTERNET
        </h2>
        <p className="font-playfair italic text-lg text-muted-foreground text-center mt-2 mb-12">
          Pulled live from Hacker News. Nobody asked the Council yet.
        </p>

        {state === 'loading' ? (
          <div className="space-y-4" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-lg bg-card/30 border border-border animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post, i) => (
              <motion.a
                key={post.id}
                href={post.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: i * 0.08, duration: 0.45, ease: 'easeOut' }}
                className="flex items-start gap-4 p-5 rounded-lg bg-card/30 border border-border transition-colors hover:border-primary/60 hover:bg-card/50"
              >
                {post.image && (
                  <img
                    src={post.image}
                    alt=""
                    loading="lazy"
                    width={40}
                    height={40}
                    className="w-10 h-10 shrink-0 rounded bg-background/60 object-contain p-1"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                <div className="min-w-0">
                  <p className="font-mono text-xs text-muted-foreground mb-2 truncate">
                    {post.domain ?? 'news.ycombinator.com'} · {post.points} points · {post.comments} comments
                  </p>
                  <p className="font-mono text-sm md:text-base text-foreground/90 leading-relaxed">
                    {post.title}
                  </p>
                </div>
              </motion.a>
            ))}
          </div>
        )}

        <p className="font-mono text-xs text-muted-foreground text-center mt-8">
          Refreshes every visit. Your idea could be next.
        </p>
      </div>
    </section>
  );
};

export default HackerNewsFeed;
